
import { supabase } from "../integrations/supabase/client";
import { Database } from "../integrations/supabase/types";

type Tier = Database["public"]["Tables"]["tier_limits"]["Row"];

export async function getTierLimits() {
  try {
    const { data, error } = await supabase
      .from('tier_limits')
      .select('*')
      .order('max_storage_mb', { ascending: true });
    
    if (error) {
      console.error("Error fetching tier limits:", error);
      throw error;
    }
    
    return data || [];
  } catch (error) {
    console.error("Error in getTierLimits:", error);
    return [];
  }
}

export async function getUserSubscriptionInfo(userId?: string) {
  try {
    let uid = userId;
    if (!uid) {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("User not authenticated");
      uid = user.id;
    }

    const { data: userData, error } = await supabase
      .from('users')
      .select('subscription_tier')
      .eq('id', uid)
      .maybeSingle();

    if (error) {
      console.error("Error fetching user subscription:", error);
      throw error;
    }
    if (!userData) {
      throw new Error("No user record found");
    }

    const { data: tierData, error: tierError } = await supabase
      .from('tier_limits')
      .select('*')
      .eq('tier', userData.subscription_tier)
      .maybeSingle();

    if (tierError) {
      console.error("Error fetching tier details:", tierError);
      throw tierError;
    }
    if (!tierData) {
      throw new Error("No tier data found");
    }

    return {
      currentTier: userData.subscription_tier,
      limits: tierData
    };

  } catch (error) {
    console.error("Error in getUserSubscriptionInfo:", error);
    return null;
  }
}

// ✅ Nueva función para calcular uso de almacenamiento
export async function calculateStorageUsed(userId: string): Promise<number> {
  try {
    const { data, error } = await supabase
      .from('media_files')
      .select('size_bytes')
      .eq('user_id', userId);
    
    if (error) {
      console.error("Error calculating storage used:", error);
      return 0;
    }
    
    const totalBytes = data?.reduce((sum, file) => sum + (file.size_bytes || 0), 0) || 0;
    return Math.round(totalBytes / (1024 * 1024)); // Convert to MB
  } catch (error) {
    console.error("Error in calculateStorageUsed:", error);
    return 0;
  }
}

export async function checkRemainingQuota(userId?: string) {
  try {
    let uid = userId;
    if (!uid) {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("User not authenticated");
      uid = user.id;
    }

    const subscriptionInfo = await getUserSubscriptionInfo(uid);
    if (!subscriptionInfo) {
      throw new Error("Could not retrieve subscription information");
    }

    const isUltimate = subscriptionInfo.currentTier === 'ultimate';

    console.log('🔍 Checking quota for user:', uid, 'tier:', subscriptionInfo.currentTier, 'isUltimate:', isUltimate);

    const { count: petCount, error: petError } = await supabase
      .from('pets')
      .select('*', { count: 'exact', head: true })
      .eq('user_id', uid);

    if (petError) {
      console.error("Error counting pets:", petError);
      throw petError;
    }

    // Degrade gracefully if media_files query fails
    let storageUsedMB = 0;
    try {
      storageUsedMB = await calculateStorageUsed(uid);
    } catch (e) {
      console.error("Storage calc failed, defaulting to 0:", e);
    }

    const maxPets = subscriptionInfo.limits.max_pets || 0;
    const maxStorageMB = subscriptionInfo.limits.max_storage_mb || 250;

    const petsRemaining = maxPets === -1 ? -1 : Math.max(0, maxPets - (petCount || 0));
    const storageRemaining = maxStorageMB === -1 ? -1 : Math.max(0, maxStorageMB - storageUsedMB);

    console.log('🔍 Quota calculation:', {
      tier: subscriptionInfo.currentTier,
      isUltimate,
      petCount,
      maxPets,
      petsRemaining,
      storageUsedMB,
      maxStorageMB,
      storageRemaining
    });

    return {
      pets: {
        used: petCount || 0,
        total: maxPets,
        remaining: petsRemaining
      },
      storage: {
        used: storageUsedMB,
        total: maxStorageMB,
        remaining: storageRemaining
      },
      tier: subscriptionInfo.currentTier,
      isUnlimited: false
    };

  } catch (error) {
    console.error("Error in checkRemainingQuota:", error);
    return null;
  }
}

// ✅ Simplified canAddPet - let the database trigger handle the enforcement
export async function canAddPet(): Promise<{ canAdd: boolean; reason?: string }> {
  try {
    console.log('🔍 canAddPet - Starting validation...');
    
    const quotaInfo = await checkRemainingQuota();
    
    if (!quotaInfo) {
      console.log('🔍 canAddPet - No quota info available');
      return { canAdd: false, reason: "Could not load subscription information" };
    }
    
    console.log('🔍 canAddPet - Quota info:', quotaInfo);
    
    
    // ✅ For non-Ultimate plans, check frontend validation but let DB be final authority
    if (quotaInfo.pets.remaining <= 0) {
      console.log('🔍 canAddPet - Pet limit reached for tier:', quotaInfo.tier);
      return { canAdd: false, reason: `Pet limit reached for your ${quotaInfo.tier} subscription tier. Please upgrade to add more pets.` };
    }
    
    console.log('🔍 canAddPet - Can add pet, remaining slots:', quotaInfo.pets.remaining);
    return { canAdd: true };
    
  } catch (error) {
    console.error("Error in canAddPet:", error);
    // ✅ If frontend check fails, let the user try - database will enforce
    return { canAdd: true };
  }
}

// ✅ Simplified function to check media upload - ONLY storage based
export async function canUploadMedia(fileSizeBytes?: number): Promise<{ canUpload: boolean; reason?: string }> {
  try {
    const quotaInfo = await checkRemainingQuota();
    
    if (!quotaInfo) {
      return { canUpload: false, reason: "Could not load subscription information" };
    }
    
    // ✅ Ultimate plan has unlimited storage
    if (quotaInfo.isUnlimited) {
      return { canUpload: true };
    }
    
    // ✅ Check storage limits if file size is provided
    if (fileSizeBytes) {
      const fileSizeMB = fileSizeBytes / (1024 * 1024);
      if (quotaInfo.storage.remaining !== -1 && fileSizeMB > quotaInfo.storage.remaining) {
        return { 
          canUpload: false, 
          reason: `Storage limit exceeded. You have ${quotaInfo.storage.remaining.toFixed(1)} MB remaining.` 
        };
      }
    }
    
    // ✅ Check if storage is full (even without file size)
    if (quotaInfo.storage.remaining !== -1 && quotaInfo.storage.remaining <= 0) {
      return { 
        canUpload: false, 
        reason: `Storage limit reached for your ${quotaInfo.tier} subscription tier.` 
      };
    }
    
    return { canUpload: true };
    
  } catch (error) {
    console.error("Error in canUploadMedia:", error);
    return { canUpload: false, reason: "Error checking media limits" };
  }
}
